<template>
    <div>
        <vrm-header />
        <vrm-title>
            <template v-slot:page-title>
                <h1 class="vrm-page-title">
                    <span>
                        {{ $t('nav.create_new_release', { i18nFallback: 'Create new release' }) }}
                    </span>
                </h1>
            </template>
        </vrm-title>
        <div class="container">
            <div
                v-html="
                    $t('firmware-library.description', {
                        i18nFallback:
                            'Creating a release includes details like specifying version details, summarizing changes or updates, and including any relevant firmware files.',
                    })
                "
            ></div>
            <br />
            <form class="vrm-form vrm-form__create-new-release">
                <span v-if="!showWizard && showSuccess" class="fragment">
                    <div class="dynamic-landing">
                        <div class="dynamic-landing__centered">
                            <img src="/img/svg-icons/success.svg" alt="success" />
                            <br />
                        </div>
                        <h1 class="dynamic-landing__centered dynamic-landing__centered__no-margin">
                            {{
                                $t('firmware-library.releaseSuccessful', {
                                    i18nFallback: 'Release successful!',
                                })
                            }}
                        </h1>
                        <div class="dynamic-landing__centered">
                            <p>
                                {{
                                    $t('firmware-library.releaseComplete', {
                                        i18nFallback:
                                            'The release is complete. See full details in the Release Overview page.',
                                    })
                                }}
                            </p>
                            <br />

                            <div class="dynamic-landing__buttons">
                                <button class="sg-btn" @click="resetWizard">
                                    {{
                                        $t('nav.create_new_release', {
                                            i18nFallback: 'Create new release',
                                        })
                                    }}
                                </button>
                                <router-link
                                    :to="{
                                        name: 'base.release-overview',
                                    }"
                                >
                                    <button class="sg-btn sg-blue">
                                        {{
                                            $t('firmware-library.goToOverview', {
                                                i18nFallback: 'Go to release overview',
                                            })
                                        }}
                                    </button>
                                </router-link>
                            </div>
                        </div>
                    </div>
                </span>
                <span v-else-if="showWizard" class="fragment">
                    <wizard
                        :value="currentId"
                        :steps="getSteps"
                        :is-edit="false"
                        :completed-until="completedUntil"
                        :can-submit="isFormValid()"
                        :next-disabled="isStepValid()"
                        :force-back-disabled="currentId === STEPS.FILES"
                        :has-change="hasChange"
                        :creating="true"
                        :submit-text="
                            $t('nav.create_new_release', { i18nFallback: 'Create new release' })
                        "
                        :has-overview="false"
                        @input="setCurrentId"
                        @reset="resetWizard"
                        @submit="onSubmit"
                        @submitAndReset="submitAndReset"
                        @resetForm="onResetForm"
                    >
                        <files
                            v-if="currentId === STEPS.FILES"
                            :initial-values="initialForm"
                            @change="handleChange"
                            @creating="handleCreate"
                        />
                        <properties
                            v-if="currentId === STEPS.PROPERTIES"
                            :initial-values="initialForm"
                            @change="handleChange"
                            @creating="handleCreate"
                        />
                        <info
                            v-if="currentId === STEPS.INFO"
                            :initial-values="initialForm"
                            @change="handleChange"
                            @creating="handleCreate"
                        />
                    </wizard>
                </span>
            </form>
        </div>
    </div>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator';
import { AngularRoute, ngInject } from '@/vue/decorators/angular-interop';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import VrmHeader from '@/vue/components/layout/VrmHeader.vue';
import VrmTitle from '@/vue/components/layout/VrmTitle.vue';
import Wizard from '@/vue/components/pages/dynamic-ess/wizard/Wizard.vue';
import Files from './forms/Files.vue';
import Properties from './forms/Properties.vue';
import Info from './forms/Info.vue';
import {
    CREATE_NEW_RELEASE_FORM_FIELDS,
    createForm,
    applyData,
    resetForm,
    NEW_RELEASE,
} from '@/vue/store/modules/create-new-release-form';
import FirmwareLibraryFileIssuesModal from '@/vue/components/modals/FirmwareLibraryFileIssuesModal.vue';
@Component({
    components: {
        Wizard,
        VrmHeader,
        VrmTitle,
        Files,
        Properties,
        Info,
        FirmwareLibraryFileIssuesModal,
    },
})
@AngularRoute('base.create-new-release', '/create-new-release', { slot: 'main' })
@AngularLocaleBundle('nav')
@AngularLocaleBundle('firmware-library')
export default class VrmFirmwareCreateNewRelease extends Vue {
    /** @type VrmModalService */
    @ngInject()
    VrmModalService;
    /** @type FirmwareLibraryService */
    @ngInject()
    FirmwareLibraryService;
    /** @type ApiService */
    @ngInject()
    ApiService;
    showWizard = true;
    showSuccess = false;
    STEPS = {
        FILES: 'files',
        PROPERTIES: 'properties',
        INFO: 'info',
    };
    currentId = this.STEPS.FILES;
    initialForm = createForm();

    hasChange = false;
    formChanges = {
        files: false,
        properties: false,
    };

    async resetWizard() {
        this.showWizard = true;
        this.showSuccess = false;
        this.currentId = this.STEPS.FILES;
        this.$store.commit(NEW_RELEASE.RESET_FORM);
        resetForm(this.initialForm);
    }

    get completedUntil() {
        let stepId = this.currentId;
        for (const step of this.getSteps) {
            const isValid = step.validator ? this.$store.getters[step.validator] : true;
            if (!isValid) {
                break;
            }
            stepId = step.id;
        }
        return this.getSteps.findIndex(i => i.id === stepId) + 1;
    }

    async setCurrentId(newId) {
        if (
            this.currentId === this.STEPS.FILES &&
            this.$store.state.createNewReleaseForm.files.length > 0 &&
            !(await this.parseFiles())
        ) {
            return;
        }
        for (const step of this.getSteps) {
            const isValid = step.validator ? this.$store.getters[step.validator] : true;
            if (step.id === newId) {
                this.currentId = newId;
                return;
            }
            if (!isValid) {
                return;
            }
        }
    }

    async parseFiles() {
        const that = this;
        return this.FirmwareLibraryService.parseFiles(
            this.$store.state.createNewReleaseForm.files,
        ).then(response => {
            if (response.data.firmwareFiles) {
                for (let [i, file] of response.data.firmwareFiles.entries()) {
                    that.$store.state.createNewReleaseForm.files[i].idLibrary = file.idLibrary;
                    that.$store.state.createNewReleaseForm.files[i].firmwareVersion =
                        file.firmwareVersion;
                    that.$store.state.createNewReleaseForm.files[i].idProducts = file.idProducts;
                    that.$store.state.createNewReleaseForm.files[i].downloadUrl = file.downloadUrl;
                }

                if (response.data.latestRelease) {
                    this.$store.commit(NEW_RELEASE.SET_FORM_FROM_API, response.data.latestRelease);
                    applyData(this.initialForm, response.data.latestRelease);
                } else {
                    this.$store.commit(NEW_RELEASE.RESET_FORM, false);
                    resetForm(this.initialForm, false);
                }
                return true;
            } else {
                if (response && response.data && !response.data.success) that.openModal(response);
                return false;
            }
        });
    }

    get getSteps() {
        return [
            {
                id: this.STEPS.FILES,
                title: this.$t('firmware-library.files', { i18nFallback: 'Upload file(s)' }),
                validator: NEW_RELEASE.FILES_VALID,
            },
            {
                id: this.STEPS.PROPERTIES,
                title: this.$t('firmware-library.properties', {
                    i18nFallback: 'Firmware properties',
                }),
                validator: NEW_RELEASE.PROPERTIES_VALID,
            },
            {
                id: this.STEPS.INFO,
                title: this.$t('firmware-library.info', { i18nFallback: 'Release info' }),
            },
        ].filter(Boolean);
    }

    isStepValid() {
        if (this.currentId === this.STEPS.FILES) {
            return !this.$store.getters[NEW_RELEASE.FILES_VALID];
        }
        if (this.currentId === this.STEPS.PROPERTIES) {
            return !this.$store.getters[NEW_RELEASE.PROPERTIES_VALID];
        }
        if (this.currentId === this.STEPS.INFO) {
            return false;
        }
        return false;
    }

    isFormValid() {
        return this.currentId === this.STEPS.INFO && this.$store.getters[NEW_RELEASE.FORM_VALID];
    }

    handleChange(value) {
        this.formChanges[value.formName] = value.hasChange;
        this.hasChange = this.formChanges.files || this.formChanges.properties;
    }

    handleCreate(value) {
        this.hasChange = value;
    }

    onResetForm() {
        this.hasChange = false;
        this.formChanges.files = false;
        this.formChanges.properties = false;
    }

    submitAndReset() {
        this.onSubmit();
        this.resetWizard();
    }

    onSubmit() {
        const data = this.$store.getters[NEW_RELEASE.TO_API_DTO];
        this.ApiService.createNewFirmwareRelease(data).then(response => {
            this.showWizard = false;
            if (response.data && response.data.success) {
                this.showSuccess = true;
            }
        });
    }

    get files() {
        return this.$store.state.createNewReleaseForm.files;
    }

    set files(value) {
        this.$store.commit(NEW_RELEASE.SET_FORM_FIELD, {
            value: value,
            key: CREATE_NEW_RELEASE_FORM_FIELDS.files,
        });
    }

    removeFileByName(fileName) {
        this.files = this.files.filter(file => file.name !== fileName);
    }

    toProperties() {
        this.setCurrentId(this.STEPS.PROPERTIES);
    }

    async openModal(response) {
        const props = {
            errors: response.data.errors,
            files: this.files,
            removeFile: this.removeFileByName,
            onContinue: this.toProperties,
        };

        // Show a modal explaining errors with parsing files
        this.VrmModalService.openVueModal(FirmwareLibraryFileIssuesModal, props);
    }
}
</script>
